import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { updateV2HoldingAllocation, updateHoldingAllocationV1 } from '../../composables/fetchAPI';
import { usePortfolio } from '../../context/PortfolioContext'; // ปรับ Path ให้ตรงกับโฟลเดอร์ของคุณ
interface EditHoldingAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  subPortfolio: any; // รับข้อมูลพอร์ตย่อยที่มีรายชื่อหุ้นมา
  onSave: () => void; // ฟังก์ชันสั่ง Refresh หน้าจอหลัก
  isSideBySide?: boolean; // ✅ รับ prop นี้
  queueInfo?: { currentIndex: number; total: number }; // ✅ เพิ่ม prop นี้เข้ามา
  openedFromAction?: string; // 👈 แก้ประเภทจาก () => void เป็น string
}

const EditHoldingAllocationModal: React.FC<EditHoldingAllocationModalProps> = ({
  isOpen,
  onClose,
  portfolioId,
  subPortfolio,
  onSave,
  isSideBySide = false, // ✅ กำหนดค่าเริ่มต้นเป็น false
  queueInfo, // ✅ รับค่าเข้ามาใช้งาน
  openedFromAction
}) => {
  // State เก็บค่า Target % ของหุ้นแต่ละตัว รูปแบบ { "AAPL": 20, "TSLA": 15 }
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [isSaving, setIsSaving] = useState(false);
  const { refreshUserPortfolios, displayMode } = usePortfolio();
  // ดึงรายชื่อหุ้นออกมา รองรับทั้งคีย์ holdings และ stocks
  const stocks = subPortfolio?.holdings || subPortfolio?.stocks || [];
const [sortOrder, setSortOrder] = useState<'default' | 'desc' | 'asc'>('desc');
  const [searchTerm, setSearchTerm] = useState(''); // ✅ เพิ่ม state นี้
  const [sortAllocations, setSortAllocations] = useState<Record<string, number>>({});
  
  // ✅ บันทึกและดึงค่า Checkbox จาก localStorage
  const STORAGE_KEY_HIDE_CLOSED = 'edit_allocation_hide_closed';
  const [hideClosedAndZero, setHideClosedAndZero] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_HIDE_CLOSED);
    return saved !== null ? JSON.parse(saved) : true;
  });
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_HIDE_CLOSED, JSON.stringify(hideClosedAndZero));
  }, [hideClosedAndZero]);
  // เมื่อเปิด Modal ให้ดึงค่า expectedRatio ปัจจุบันมาเซ็ตเป็นค่าเริ่มต้น
  useEffect(() => {
    if (isOpen && stocks.length > 0) {
      const initialAllocations: Record<string, number> = {};
      stocks.forEach((stock: any) => {
        const symbol = stock.assetSymbol || stock.stockSymbol || stock.symbol;
        initialAllocations[symbol] = stock.expectedRatio || 0;
      });
      setAllocations(initialAllocations);
      // ✅ 2. เซ็ตค่าให้ sortAllocations มีข้อมูลพร้อมเรียงจากมากไปน้อยทันทีที่เปิด
      setSortAllocations(initialAllocations);
      // ✅ 3. บังคับเซ็ต sortOrder เป็น 'desc' ทุกครั้งที่เปิด Modal ขึ้นมาใหม่
      setSortOrder('desc');

      // 🌟 ถ้าเปิดมาจากการทำรายการซื้อขาย (TRADE) ให้ติ๊กซ่อนหุ้นที่ปิดสถานะแล้วเสมอ
      // หุ้นที่เพิ่งขายหมดถูกตั้งสัดส่วนเป็น 0% ไว้ให้แล้วตั้งแต่ SellBuyStockModal (ตอนบันทึกจะส่ง 0% ไปด้วย) เลยไม่ต้องโชว์
      if (openedFromAction === 'TRADE') {
        setHideClosedAndZero(true);
      }
      
    }
  }, [isOpen, stocks,openedFromAction]);

  if (!isOpen) return null;

  // คำนวณผลรวมของ Target %
 const totalAllocation = Object.values(allocations).reduce((sum, val) => sum + (Number(val) || 0), 0);
  const isOverAllocated = totalAllocation > 100;

  // จัดการเมื่อพิมพ์ตัวเลข
  const handleAllocationChange = (symbol: string, value: string) => {
    let numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) numValue = 0;

    setAllocations(prev => ({
      ...prev,
      [symbol]: numValue
    }));
  };
  const filteredStocks = stocks.filter((stock: any) => {
    // ✅ 1. กรองหุ้นที่ปิดสถานะแล้วออกก่อน (ถ้าสถานะเป็น CLOSED ให้ข้ามทันที)
   if (hideClosedAndZero && (stock?.status === 'CLOSED' || stock?.value === 0)) {
      return false;
    }
    // ดึงค่าออกมา
    const symbol = (stock.assetSymbol || stock.stockSymbol || stock.symbol || '').toLowerCase();
    const name = (stock.displayName || stock.name || '').toLowerCase();

    // แปลงคำค้นหาเป็นตัวเล็กทั้งหมดก่อนเทียบ
    const search = searchTerm.toLowerCase();

    // ตรวจสอบว่ามีคำที่ค้นหาอยู่ใน symbol หรือ name หรือไม่
    return (
      symbol.includes(search) ||
      name.includes(search)
    );
  });

const displayedStocks = [...filteredStocks].sort((a, b) => {
    if (sortOrder === 'default') return 0;

    const symbolA = a.assetSymbol || a.stockSymbol || a.symbol;
    const symbolB = b.assetSymbol || b.stockSymbol || b.symbol;
   const ratioA = sortAllocations[symbolA] || 0;
    const ratioB = sortAllocations[symbolB] || 0;

    if (sortOrder === 'desc') {
      return ratioB - ratioA; // มากไปน้อย
    } else {
      return ratioA - ratioB; // น้อยไปมาก
    }
  });
// ✅ ฟังก์ชันสลับสถานะปุ่ม Sort
 const handleToggleSort = () => {
    // เมื่อกดปุ่ม Sort ให้ซิงค์ค่าล่าสุดมาก่อน เพื่อให้การเรียงแม่นยำ
    setSortAllocations({ ...allocations });

    if (sortOrder === 'default') setSortOrder('desc');
    else if (sortOrder === 'desc') setSortOrder('asc');
    else setSortOrder('default');
  };


  const handleInputBlur = (symbol: string) => {
    setSortAllocations(prev => ({
      ...prev,
      [symbol]: allocations[symbol] || 0
    }));
  };

  // ฟังก์ชันกดบันทึก
  const handleSave = async () => {
    if (isOverAllocated) {
      toast.error('สัดส่วนรวมต้องไม่เกิน 100%');
      return;
    }

    setIsSaving(true);
    try {
      const portId = portfolioId;
      const subId = subPortfolio.subPortId;

      if (displayMode === 'V1') {
        // ✅ LOGIC V1 ตาม PDF หน้า 4 Section 8
        // ใช้ assetId เป็นตัวระบุ holding ตาม Spec
        const holdingsPayload = stocks.map((stock: any) => {
          const symbol = stock.assetSymbol || stock.stockSymbol || stock.symbol;
          return {
            assetId: stock.stockSymbol, // ส่ง stockSymbol เข้าไปเป็น assetId ตาม Spec
            expectedRatio: allocations[symbol] || 0
          };
        });

        await updateHoldingAllocationV1(portId, subId, holdingsPayload);
      } else {
        // 🔵 LOGIC V2 (เดิม)
        const payload = stocks.map((stock: any) => {
          const symbol = stock.assetSymbol || stock.stockSymbol || stock.symbol;
          return {
            assetId: stock.assetId || `asset_stock_${symbol.toLowerCase()}`,
            expectedRatio: allocations[symbol] || 0
          };
        });
        await updateV2HoldingAllocation(portId, subId, payload);
      }

      await refreshUserPortfolios();
      toast.success('อัปเดตสัดส่วนหุ้นเรียบร้อย');
      onSave();
      // onClose();
    } catch (error) {
      console.error(error);
      toast.error('ไม่สามารถอัปเดตสัดส่วนได้');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center p-4 z-[100] ${isSideBySide ? 'pointer-events-none' : ' pointer-events-auto'}`}>

      {/* 2. ถ้าไม่ใช่ Side-by-Side ให้ตัวครอบ Backdrop รับ Event ปกติ */}
      {!isSideBySide && (
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
          onClick={onClose}
        ></div>
      )}

      {/* 3. ตัว Modal หลักต้องเป็น pointer-events-auto เสมอ */}
      <div
        className={`relative rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.5)] w-full max-w-lg border flex flex-col pointer-events-auto ${isSideBySide ? 'md:translate-x-[52%] h-[480px]' : ''} max-h-[95vh] overflow-hidden`}
        style={{ backgroundColor: 'var(--component-bg-color)', borderColor: 'var(--border-item-light)', zIndex: 105 }} // ✅ ย้าย z-index มาที่ตัว Modal ตรงๆ
        onClick={(e) => e.stopPropagation()}
      >
        {/* --- Header --- */}
     <div className="flex justify-between items-center p-5 border-b" style={{ borderColor: 'var(--border-item-light)' }}>
          <div className="flex flex-col gap-1">
            {/* ✅ แสดงป้ายบอกลำดับคิว */}
            {queueInfo && queueInfo.total > 1 && (
              <div>
                <span className="bg-purple-500/20 text-purple-400 border border-purple-500/40 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  ({queueInfo.currentIndex} จาก {queueInfo.total})
                </span>
              </div>
            )}
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-color)' }}>
              สัดส่วนพอร์ตย่อย: <span style={{ color: 'var(--yellow-text-color)' }}>{subPortfolio?.name}</span>
            </h2>
          </div>

          <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors font-bold text-xl">
            &#x2715;
          </button>
        </div>

        {/* --- Progress Bar --- */}
        <div className="px-5 py-2" style={{ backgroundColor: 'var(--bg-item)' }}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>สัดส่วนรวม (Total Target)</span>
            <span className={`text-sm font-bold px-2 py-0.5 rounded ${isOverAllocated ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20'}`} style={{ color: !isOverAllocated ? 'var(--green-text-color)' : '' }}>
              {totalAllocation.toFixed(2)}% / 100%
            </span>
          </div>
          <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-item-light)' }}>
            <div
              className={`h-full transition-all duration-300 ${isOverAllocated ? 'bg-red-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(totalAllocation, 100)}%`, backgroundColor: !isOverAllocated ? 'var(--green-btn)' : '' }}
            />
          </div>
          {isOverAllocated && (
            <p className="text-xs text-red-500 mt-2 text-right">** สัดส่วนรวมเกิน 100% กรุณาปรับลด **</p>
          )}
        </div>

        {/* --- List of Stocks --- */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3 no-scrollbar">
          {/* --- Search Bar --- */}
          <div className="pb-1 flex flex-row gap-2">
            <input
              type="text"
              placeholder="ค้นหาชื่อย่อ หรือชื่อบริษัท..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--component-bg-color)',
                borderColor: 'var(--border-item-light)',
                color: 'var(--text-color)',
                outlineColor: 'var(--yellow-text-color)'
              }}
            />

            {/* ✅ ปุ่มกด Sort สัดส่วนเป้าหมาย */}
           <button
              onClick={handleToggleSort}
              className={`px-3 py-2 rounded-lg border text-xs font-bold transition-all flex items-center gap-1 shrink-0 ${
                sortOrder !== 'default'
                  ? 'bg-yellow-400 text-black border-yellow-400 shadow'
                  : 'bg-gray-800 text-gray-300 border-gray-600 hover:border-gray-400'
              }`}
              title="เรียงตามสัดส่วนเป้าหมาย"
            >
              <span>📊 เรียงสัดส่วน</span>
              <span>
                {sortOrder === 'desc' ? '▼' : sortOrder === 'asc' ? '▲' : '↕'}
              </span>
            </button>
          </div>
<div className="flex items-center gap-2 px-1">
              <input
                type="checkbox"
                id="hideClosedToggle"
                checked={hideClosedAndZero}
                onChange={(e) => setHideClosedAndZero(e.target.checked)}
                className="w-4 h-4 cursor-pointer accent-yellow-500 rounded border-gray-600 bg-gray-700"
              />
              <label htmlFor="hideClosedToggle" className="text-xs text-gray-400 cursor-pointer select-none hover:text-white transition-colors">
                ซ่อนหุ้นที่ปิดสถานะแล้ว (Closed / มูลค่าเป็น 0)
              </label>
            </div>

          {displayedStocks.length === 0 ? (
            <div className="text-center py-10" style={{ color: 'var(--secondary-text-color)' }}>ไม่มีหุ้นในพอร์ตย่อยนี้</div>
          ) : (
            displayedStocks.map((stock: any) => {
              const symbol = stock.assetSymbol || stock.stockSymbol || stock.symbol;
              const name = stock.displayName || stock.name || symbol;

              return (
                <div
                  key={symbol}
                  className="flex items-center justify-between px-3 py-1.5 rounded-lg border transition-colors hover:shadow-sm"
                  style={{ backgroundColor: 'var(--bg-color)', borderColor: 'var(--border-item-light)' }}
                >
                  <div className="flex flex-col overflow-hidden mr-4">
                    <span className="font-bold text-base" style={{ color: 'var(--text-color)' }}>{symbol}</span>
                    <span className="text-xs truncate" style={{ color: 'var(--secondary-text-color)' }}>{name}</span>
                  </div>

                  <div className="flex flex-row items-end shrink-0">
                    <label className="text-[10px] mb-1 mr-1 font-bold" style={{ color: 'var(--secondary-text-color)' }}>สัดส่วนเป้าหมาย %</label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={allocations[symbol] === 0 ? '' : allocations[symbol]}
                        onChange={(e) => handleAllocationChange(symbol, e.target.value)}
                        onBlur={() => handleInputBlur(symbol)} // อัปเดตลำดับ Sort เมื่อพิมพ์เสร็จและกดคลิกออก
                        placeholder="0.0"
                        className="w-24 text-right pr-6 pl-2 py-1.5 rounded-md border focus:outline-none focus:ring-2 font-bold"
                        style={{
                          backgroundColor: 'var(--component-bg-color)',
                          color: 'var(--text-color)',
                          borderColor: 'var(--border-item-light)',
                          outlineColor: 'var(--yellow-text-color)'
                        }}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: 'var(--secondary-text-color)' }}>%</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* --- Footer Buttons --- */}
        <div className="p-4 border-t flex justify-end gap-3" style={{ borderColor: 'var(--border-item-light)', backgroundColor: 'var(--bg-item)' }}>
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg font-bold transition-colors"
            style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--button-text-color)' }}
          >
            ยกเลิก
          </button>
          <button
            onClick={handleSave}
            disabled={isOverAllocated || isSaving || stocks.length === 0}
            className={`px-8 py-2 rounded-lg font-bold shadow-lg transition-transform ${isOverAllocated || stocks.length === 0 ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
            style={{
              backgroundColor: 'var(--button-edit-bg)',
              color: 'var(--text-black)'
            }}
          >
            {isSaving ? 'กำลังบันทึก...' : 'บันทึกสัดส่วน'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditHoldingAllocationModal;